// frontend/src/pages/AnalysisHistoryPage/AnalysisHistoryPage.jsx

import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';

import { UIContext } from '../../contexts/UIContext';
import { AuthContext } from '../../contexts/AuthContext';

import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import HistoryItem from '../../components/HistoryItem/HistoryItem';
import HistoryListHeader from '../../components/HistoryListHeader/HistoryListHeader';
import ExplanationModal from '../../components/ExplanationModal/ExplanationModal';
import HistoryFilters from '../../components/HistoryFilters/HistoryFilters';
import HeatmapModal from '../../components/HeatmapModal/HeatmapModal';
import HistoryTextInspectionModal from '../../components/TextInspectionModal/HistoryTextInspectionModal';

import translationEn from '../../i18n/supported_languages/en/translation.json';

import './AnalysisHistoryPage.css';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';

const ALL_CONTENT_CATEGORIES = Object.keys(translationEn.contentMetadata.inclusion);

const INITIAL_FILTERS = {
    contentTypes: ['text', 'image', 'audio', 'video'],
    scoreTypes: [],
    likelihoods: ['Yes', 'No'],
    contentCategories: ALL_CONTENT_CATEGORIES,
    safetyFlags: [], 
    filenameSearch: '',
    startDate: '',
    endDate: ''
};

const calculateLocalVetraScore = (score1, score2) => {
    if (score1 == null || score2 == null) return null;
    const p_1 = score1 / 100.0;
    const p_2 = score2 / 100.0;
    const prob_or = 1 - ((1 - p_1) * (1 - p_2));
    const synergy_bonus = Math.pow(p_1 * p_2, 0.5) * 0.15;
    return Math.floor(Math.min(prob_or + synergy_bonus, 1.0) * 100);
};

const areArraysEqual = (arr1, arr2) => {
    if (!arr1 || !arr2) return arr1 === arr2;
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
    
    if (arr1.length !== arr2.length) return false;
    const sortedArr1 = [...arr1].sort();
    const sortedArr2 = [...arr2].sort();
    return sortedArr1.every((value, index) => value === sortedArr2[index]);
};

const AnalysisHistoryPage = () => {
    const { t, i18n } = useTranslation();
    const { setIsFaqModalOpen } = useContext(UIContext);
    const { fetchWithAuth, isAuthenticated, user } = useContext(AuthContext);

    // --- TANSTACK QUERY: Server State for Tasks Config ---
    const { data: tasksConfigData } = useQuery({
        queryKey: ['tasksConfig'],
        queryFn: async () => {
            const res = await fetch('/api/config/tasks');
            if (!res.ok) throw new Error('Failed to load tasks config');
            return res.json();
        },
        staleTime: Infinity, // Configuration rarely changes
    });

    // Wrap both in useMemo to stabilize their references
    const tasksConfig = useMemo(() => tasksConfigData?.tasks || {}, [tasksConfigData]);
    const masterTaskOrder = useMemo(() => tasksConfigData?.tasks ? Object.keys(tasksConfigData.tasks) : [], [tasksConfigData]);

    // --- TANSTACK QUERY: Server State for History Data ---
    const { 
        data: historyData = [], 
        isLoading: isHistoryLoading, 
        error: historyErrorObj 
    } = useQuery({
        queryKey: ['userHistory'],
        queryFn: async () => {
            const response = await fetchWithAuth('/api/user/history');
            if (!response.ok) throw new Error(`Failed to fetch history. Status: ${response.status}`);
            return response.json();
        },
        enabled: !!isAuthenticated && !user?.is_guest,
    });

    const historyError = historyErrorObj?.message || null;

    // --- Local Client State Management ---
    const [historyFilters, setHistoryFilters] = useState(INITIAL_FILTERS);
    const [hasInitializedFilters, setHasInitializedFilters] = useState(false);
    const [visibleCount, setVisibleCount] = useState(5);
    const [isExplanationModalOpen, setIsExplanationModalOpen] = useState(false);
    const [currentExplanation, setCurrentExplanation] = useState('');
    const [explanationTitle, setExplanationTitle] = useState('');
    const [isExplanationStructured, setIsExplanationStructured] = useState(false);
    const [explanationScore, setExplanationScore] = useState(null);
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    const [historyItemsKey, setHistoryItemsKey] = useState(0);
    const [isHeatmapModalOpen, setIsHeatmapModalOpen] = useState(false);
    const [heatmapModalData, setHeatmapModalData] = useState({ imageSrc: '', regions: [], taskName: '', score: null, mode: 'full' });
    const [isTextInspectionModalOpen, setIsTextInspectionModalOpen] = useState(false);
    const [textInspectionModalData, setTextInspectionModalData] = useState({ regions: [], taskName: '', score: null });
    const [translatedModalExplanation, setTranslatedModalExplanation] = useState(null);
    const [isCheckingModalCache, setIsCheckingModalCache] = useState(false);
    const [isTranslatingModal, setIsTranslatingModal] = useState(false);
    const [modalTranslationError, setModalTranslationError] = useState(null);
    const [explanationTaskKey, setExplanationTaskKey] = useState('digital_integrity_score');

    // Calculate available filter tasks based on the history data
    const availableFilterTasks = useMemo(() => {
        if (!masterTaskOrder || masterTaskOrder.length === 0) return [];

        const defaultFallback = ['digital_integrity_score', 'ai_detection', 'fake_news_detection']
            .filter(t => masterTaskOrder.includes(t));

        if (!historyData || historyData.length === 0) {
            return defaultFallback;
        }

        const foundTasks = new Set();
        historyData.forEach(item => {
            const itemTasks = Object.keys(item.tasks || {});
            itemTasks.forEach(t => foundTasks.add(t));
            Object.entries(tasksConfig || {}).forEach(([metaKey, metaConfig]) => {
                if (metaConfig.is_meta_task) {
                    const deps = metaConfig.depends_on || [];
                    if (deps.length > 0 && deps.every(d => itemTasks.includes(d))) {
                        foundTasks.add(metaKey);
                    }
                }
            });
        });

        if (foundTasks.size === 0) return defaultFallback;
        return masterTaskOrder.filter(task => foundTasks.has(task));
    }, [historyData, tasksConfig, masterTaskOrder]);

    const dynamicInitialFilters = useMemo(() => {
        const initial = { ...INITIAL_FILTERS, scoreTypes: availableFilterTasks };
        availableFilterTasks.forEach(taskKey => {
            initial[`score_${taskKey}`] = [0, 100];
        });
        return initial;
    }, [availableFilterTasks]);

    // Initialize filters once data loads
    useEffect(() => {
        if (historyData.length > 0 && !hasInitializedFilters && availableFilterTasks.length > 0) {
            setHistoryFilters(dynamicInitialFilters);
            setHasInitializedFilters(true);
        }
    }, [historyData, hasInitializedFilters, availableFilterTasks, dynamicInitialFilters]);

    // --- DERIVED STATE: Highly Optimized Local Filtering Logic ---
    const filteredHistoryData = useMemo(() => {
        if (!historyData || historyData.length === 0) return [];
        let results = [...historyData];

        if (historyFilters.contentTypes.length === 0) { return []; }
        else if (historyFilters.contentTypes.length < 4) { 
            results = results.filter(item => historyFilters.contentTypes.includes(item.content_type)); 
        }

        if (historyFilters.scoreTypes.length === 0) { return []; }
        else if (historyFilters.scoreTypes.length < masterTaskOrder.length) { 
            results = results.filter(item => {
                const itemKeys = Object.keys(item.tasks || {});
                Object.entries(tasksConfig || {}).forEach(([metaKey, config]) => {
                    if (config.is_meta_task && config.depends_on && config.depends_on.every(d => itemKeys.includes(d))) {
                        itemKeys.push(metaKey);
                    }
                });
                return itemKeys.some(taskKey => historyFilters.scoreTypes.includes(taskKey));
            }); 
        }

        if (historyFilters.likelihoods.length < 2 && historyFilters.likelihoods.length > 0) {
            results = results.filter(item => {
                const tasks = Object.values(item.tasks || {});
                if (tasks.length === 0) return false;
                return tasks.every(task => historyFilters.likelihoods.includes(task.likelihood));
            });
        } else if (historyFilters.likelihoods.length === 0) {
            return [];
        }

        results = results.filter(item => {
            return historyFilters.scoreTypes.every(scoreType => {
                let task = item.tasks[scoreType];
                if (!task && tasksConfig?.[scoreType]?.is_meta_task) {
                    const deps = tasksConfig[scoreType].depends_on || [];
                    if (deps.length === 2 && item.tasks[deps[0]] && item.tasks[deps[1]]) {
                        const metaScore = calculateLocalVetraScore(item.tasks[deps[0]].score, item.tasks[deps[1]].score);
                        task = { score: metaScore };
                    }
                }
                const scoreRange = historyFilters[`score_${scoreType}`];
                if (!task || !scoreRange) return true;
                return task.score >= scoreRange[0] && task.score <= scoreRange[1];
            });
        });

        const isAllCategoriesSelected = historyFilters.contentCategories.length === ALL_CONTENT_CATEGORIES.length;
        if (!isAllCategoriesSelected) {
            results = results.filter(item => {
                const tasks = item.tasks || {};
                const metadata = Object.values(tasks).find(t => t?.content_metadata)?.content_metadata;
                if (!metadata || !metadata.category) return false;
                return historyFilters.contentCategories.includes(metadata.category);
            });
        }

        if (historyFilters.safetyFlags.length > 0) {
            results = results.filter(item => {
                const tasks = item.tasks || {};
                const metadata = Object.values(tasks).find(t => t?.content_metadata)?.content_metadata;
                if (!metadata || !metadata.safety_flags) return false;
                return metadata.safety_flags.some(flag => historyFilters.safetyFlags.includes(flag));
            });
        }

        if (historyFilters.filenameSearch) {
            const searchTerm = historyFilters.filenameSearch.toLowerCase();
            const hashRegex = /-[a-f0-9]{6}(\.[a-z0-9]+)$/i;
            results = results.filter(item => {
                const rawFilename = item.source_filename || '';
                const cleanFilename = rawFilename.replace(hashRegex, '$1');
                return cleanFilename.toLowerCase().includes(searchTerm);
            });
        }

        const { startDate, endDate } = historyFilters;
        if (startDate) { const start = new Date(startDate); results = results.filter(item => new Date(item.latest_timestamp) >= start); }
        if (endDate) { const end = new Date(endDate); end.setHours(23, 59, 59, 999); results = results.filter(item => new Date(item.latest_timestamp) <= end); }

        results.sort((a, b) => new Date(b.latest_timestamp) - new Date(a.latest_timestamp));

        return results;
    }, [historyData, historyFilters, masterTaskOrder.length, tasksConfig]);

    // Filename options logic: Derived from raw data with hash cleanup
    const filenameOptions = useMemo(() => {
        if (!historyData || historyData.length === 0) return [];
        const hashRegex = /-[a-f0-9]{6}(\.[a-z0-9]+)$/i;
        const allFilenames = historyData.map(item => {
            const rawName = item.source_filename;
            if (!rawName) return null;
            return rawName.replace(hashRegex, '$1');
        }).filter(Boolean);
        return Array.from(new Set(allFilenames)).sort();
    }, [historyData]);

    useEffect(() => {
        setVisibleCount(5);
    }, [filteredHistoryData]);

    const areFiltersApplied = useMemo(() => {
        if (!dynamicInitialFilters || dynamicInitialFilters.scoreTypes.length === 0) return false;
        
        if (historyFilters.filenameSearch !== dynamicInitialFilters.filenameSearch ||
            historyFilters.startDate !== dynamicInitialFilters.startDate ||
            historyFilters.endDate !== dynamicInitialFilters.endDate) {
            return true;
        }
        if (!areArraysEqual(historyFilters.contentTypes, dynamicInitialFilters.contentTypes) ||
            !areArraysEqual(historyFilters.scoreTypes, dynamicInitialFilters.scoreTypes) ||
            !areArraysEqual(historyFilters.likelihoods, dynamicInitialFilters.likelihoods) ||
            !areArraysEqual(historyFilters.contentCategories, dynamicInitialFilters.contentCategories) ||
            !areArraysEqual(historyFilters.safetyFlags, dynamicInitialFilters.safetyFlags)
        ) {
            return true;
        }
        for (const taskKey of masterTaskOrder) {
            const currentSlider = historyFilters[`score_${taskKey}`] || [0, 100];
            if (!areArraysEqual(currentSlider, [0, 100])) return true;
        }
        return false;
    }, [historyFilters, dynamicInitialFilters, masterTaskOrder]);

    const currentDrawerWidth = areFiltersApplied ? 420 : 360;

    const handleOpenFilters = () => setIsFilterDrawerOpen(true);
    const handleCloseFilters = () => setIsFilterDrawerOpen(false);

    const handleFilterChange = (name, value) => {
        const sanitizedValue = value || '';
        setHistoryFilters(prevFilters => ({
            ...prevFilters,
            [name]: sanitizedValue
        }));
    };

    const handleResetFilters = () => {
        setHistoryFilters(dynamicInitialFilters); 
        setHistoryItemsKey(prevKey => prevKey + 1);
    };

    const loadMore = () => setVisibleCount(prevCount => prevCount + 5);

    const handleShowExplanation = useCallback((taskName, explanation, isStructured, score, rawTaskKey) => {
        const translatedTitle = t('explanationModal.title', { taskName });
        setExplanationTitle(translatedTitle);
        setCurrentExplanation(explanation);
        setIsExplanationStructured(isStructured);
        setExplanationScore(score);
        setExplanationTaskKey(rawTaskKey || 'digital_integrity_score');
        setIsExplanationModalOpen(true);

        const checkCache = async () => {
            if (i18n.language.startsWith('en') || isStructured || !explanation) {
                return;
            }
            setIsCheckingModalCache(true);
            try {
                const response = await fetchWithAuth('/api/translate', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        text: explanation,
                        target_language: i18n.language,
                        cache_only: true
                    })
                });
                if (response.ok) {
                    const data = await response.json();
                    if (data.translated_text) {
                        setTranslatedModalExplanation(data.translated_text);
                    }
                }
            } catch (err) {
                console.error("Modal cache check failed:", err);
            } finally {
                setIsCheckingModalCache(false);
            }
        };

        checkCache();
    }, [t, i18n.language, fetchWithAuth]);

    const handleCloseExplanation = () => {
        setIsExplanationModalOpen(false);
        setTranslatedModalExplanation(null);
        setIsCheckingModalCache(false);
        setIsTranslatingModal(false);
        setModalTranslationError(null);
    };

    const handleTranslateModalExplanation = useCallback(async () => {
        if (!currentExplanation) return;
        setIsTranslatingModal(true);
        setModalTranslationError(null);
        try {
            const response = await fetchWithAuth('/api/translate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    text: currentExplanation,
                    target_language: i18n.language
                })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Translation failed');
            setTranslatedModalExplanation(data.translated_text);
        } catch (err) {
            setModalTranslationError(err.message);
        } finally {
            setIsTranslatingModal(false);
        }
    }, [currentExplanation, i18n.language, fetchWithAuth]);

    const historyToDisplay = filteredHistoryData.slice(0, visibleCount);
    const hasMore = visibleCount < filteredHistoryData.length;

    const handleShowHeatmap = (imageSrc, regions, taskName, score, footerNote = null) => {
        setHeatmapModalData({ imageSrc, regions, taskName, score, footerNote, mode: 'history' });
        setIsHeatmapModalOpen(true);
    };

    const handleShowTextInspection = (regions, taskName, score) => {
        setTextInspectionModalData({ regions, taskName, score, mode: 'history' });
        setIsTextInspectionModalOpen(true);
    };

    const renderContent = () => {
        if (isHistoryLoading) return <div className="history-status loading"><CircularProgress color="secondary" /></div>;
        if (historyError) return <div className="history-status error">{historyError}</div>;
        if (historyData.length === 0) {
            return (
                <div className="history-empty">
                    <p>{t('historyPage.empty.message')}</p>
                    <Button component={Link} to="/app" variant="contained" className="primary-button">{t('historyPage.empty.cta')}</Button>
                </div>
            );
        }
        if (filteredHistoryData.length > 0) {
            return (
                <div className="history-items-container">
                    {historyToDisplay.map((item) => (
                        <HistoryItem
                            key={`${item.exact_hash}-${item.context_hash || 'no-ctx'}-${historyItemsKey}`}
                            item={item}
                            onShowExplanation={handleShowExplanation}
                            onShowHeatmap={handleShowHeatmap}
                            onShowTextInspection={handleShowTextInspection}
                            activeScoreFilters={historyFilters.scoreTypes}
                        />
                    ))}
                </div>
            );
        }
        return (
            <div className="history-empty filtered">
                <p>{t('historyPage.filteredEmpty.message')}</p>
                <Button onClick={handleResetFilters} variant="outlined" className="load-more-button">
                    {t('historyPage.filteredEmpty.cta')}
                </Button>
            </div>
        );
    };

    return (
        <div className={`history-page-wrapper ${isFilterDrawerOpen ? 'content-blurred-subtle' : ''} ${isExplanationModalOpen ? 'app-content-blurred' : ''}`}>
            <Helmet>
                <title>{t('historyPage.helmet.title')}</title>
            </Helmet>
            <main 
                className="history-main-content"
                style={{ 
                    marginLeft: (isFilterDrawerOpen && window.innerWidth > 900) ? `${currentDrawerWidth}px` : '0', 
                    width: (isFilterDrawerOpen && window.innerWidth > 900) ? `calc(100% - ${currentDrawerWidth}px)` : '100%',
                    transition: 'margin-left 0.3s ease, width 0.3s ease',
                    boxSizing: 'border-box'
                }}
            >
                <Header showDataFlowPreview={false} showBackButton={true} />
                <div className="history-content-inner">
                    <h1>{t('historyPage.title')}</h1>
                    <p style={{ color: 'var(--secondary-color)' }}>
                        {t('historyPage.description')}
                    </p>
                    {!isHistoryLoading && historyData.length > 0 && (
                        <HistoryListHeader
                            onOpenFilters={handleOpenFilters}
                            onClearFilters={handleResetFilters}
                            areFiltersApplied={areFiltersApplied}
                        />
                    )}

                    {renderContent()}

                    {hasMore && !isHistoryLoading && (
                        <div className="load-more-container">
                            <Button onClick={loadMore} variant="outlined" className="load-more-button">{t('historyPage.loadMore')}</Button>
                        </div>
                    )}
                </div>
                <Footer onOpenFaqModal={() => setIsFaqModalOpen(true)} showFaqButton={true} />
            </main>
            
            <Drawer anchor="left" open={isFilterDrawerOpen} onClose={handleCloseFilters} PaperProps={{ sx: { backgroundColor: 'var(--tertiary-color)' } }} >
                <Box sx={{ width: currentDrawerWidth, padding: 2.5, height: '100%', display: 'flex', flexDirection: 'column', transition: 'width 0.3s ease' }} role="presentation" >
                    <HistoryFilters
                        filters={historyFilters} 
                        onFilterChange={handleFilterChange} 
                        onResetFilters={handleResetFilters} 
                        onClose={handleCloseFilters}
                        filenameOptions={filenameOptions}
                        tasksConfig={tasksConfig}
                        masterTaskOrder={masterTaskOrder}
                        dynamicInitialFilters={dynamicInitialFilters}
                        availableFilterTasks={availableFilterTasks}
                    />
                </Box>
            </Drawer>

            <ExplanationModal
                isOpen={isExplanationModalOpen}
                onClose={handleCloseExplanation}
                title={explanationTitle}
                explanation={currentExplanation}
                isStructured={isExplanationStructured}
                score={explanationScore}
                taskKey={explanationTaskKey}
                translatedExplanation={translatedModalExplanation}
                isCheckingCache={isCheckingModalCache}
                isTranslating={isTranslatingModal}
                translationError={modalTranslationError}
                onTranslate={handleTranslateModalExplanation}
            />

            <HeatmapModal
                isOpen={isHeatmapModalOpen}
                onClose={() => setIsHeatmapModalOpen(false)}
                imageSrc={heatmapModalData.imageSrc}
                regions={heatmapModalData.regions}
                taskName={heatmapModalData.taskName}
                score={heatmapModalData.score}
                mode={heatmapModalData.mode}
                footerNote={heatmapModalData.footerNote}
            />

            <HistoryTextInspectionModal
                isOpen={isTextInspectionModalOpen}
                onClose={() => setIsTextInspectionModalOpen(false)}
                regions={textInspectionModalData.regions}
                taskName={textInspectionModalData.taskName}
                score={textInspectionModalData.score}
            />

        </div>
    );
};

export default AnalysisHistoryPage;